import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './MenuManagement.css';

// ─── Tag system ──────────────────────────────
const ALLERGEN_TAGS = [
    { label: 'Vegan', icon: '🌱', color: '#22c55e' },
    { label: 'Jain', icon: '🙏', color: '#f59e0b' },
    { label: 'Contains Nuts', icon: '🥜', color: '#ef4444' },
    { label: 'Gluten Free', icon: '🌾', color: '#06b6d4' },
    { label: 'Spicy', icon: '🌶️', color: '#dc2626' },
    { label: 'Dairy Free', icon: '🥛', color: '#8b5cf6' },
    { label: 'Sugar Free', icon: '🚫', color: '#ec4899' },
    { label: 'Chef Special', icon: '⭐', color: '#eab308' },
];

const CATEGORY_ICONS = ['🍽️', '🥗', '🍖', '🍛', '🍰', '🍹', '🥘', '🍕', '🧁', '🍦', '☕', '🍷', '🥂', '🍺'];

interface MenuItem {
    name: string;
    description: string;
    tags: string[];
    isVeg: boolean;
    isAvailable: boolean;
}

interface MenuCategory {
    _id?: string;
    name: string;
    icon: string;
    order: number;
    dayLabel: string;
    scheduledDate: string;
    isBeverage: boolean;
    items: MenuItem[];
    isActive: boolean;
}

const emptyCategory: MenuCategory = {
    name: '', icon: '🍽️', order: 0, dayLabel: '', scheduledDate: '', isBeverage: false, items: [], isActive: true,
};

const emptyItem: MenuItem = {
    name: '', description: '', tags: [], isVeg: true, isAvailable: true,
};

const MenuManagement: React.FC = () => {
    const { accessToken, loading: authLoading } = useAuth();
    const history = useHistory();

    const [categories, setCategories] = useState<MenuCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<MenuCategory>(emptyCategory);
    const [saving, setSaving] = useState(false);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/menu/admin/all`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await res.json();
            if (res.ok) setCategories(data.categories || []);
        } catch (err) {
            console.error('Fetch menu error:', err);
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        if (!authLoading) {
            if (accessToken) fetchCategories();
            else history.push('/login');
        }
    }, [accessToken, authLoading, fetchCategories, history]);

    // ─── CRUD ────────────────────────────────────
    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (saving) return;
        if (!editing.name.trim()) { alert('Category name is required'); return; }
        setSaving(true);

        const isNew = !editing._id;
        const url = isNew ? `${API_BASE_URL}/api/menu` : `${API_BASE_URL}/api/menu/${editing._id}`;
        const method = isNew ? 'POST' : 'PUT';

        try {
            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
                body: JSON.stringify(editing),
            });
            if (res.ok) { setModalOpen(false); fetchCategories(); }
            else { const d = await res.json(); alert(d.message || 'Save failed'); }
        } catch { alert('Network error'); } finally { setSaving(false); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this entire category and all its items?')) return;
        try {
            await fetch(`${API_BASE_URL}/api/menu/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            fetchCategories();
        } catch { alert('Delete failed'); }
    };

    const handleToggleItem = async (catId: string, idx: number) => {
        try {
            await fetch(`${API_BASE_URL}/api/menu/${catId}/item/${idx}/toggle`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            fetchCategories();
        } catch { alert('Toggle failed'); }
    };

    // ─── Item helpers ────────────────────────────
    const addItem = () => setEditing(p => ({ ...p, items: [...p.items, { ...emptyItem }] }));
    const removeItem = (idx: number) => setEditing(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));
    const updateItem = (idx: number, field: string, value: any) =>
        setEditing(p => ({ ...p, items: p.items.map((it, i) => i === idx ? { ...it, [field]: value } : it) }));
    const toggleTag = (idx: number, tag: string) =>
        setEditing(p => ({
            ...p,
            items: p.items.map((it, i) => {
                if (i !== idx) return it;
                const tags = it.tags.includes(tag) ? it.tags.filter(t => t !== tag) : [...it.tags, tag];
                return { ...it, tags };
            }),
        }));

    if (loading) {
        return (
            <div className="menu-mgmt-page">
                <div className="menu-loading"><div className="menu-spinner"></div><p>Loading menu...</p></div>
            </div>
        );
    }

    return (
        <div className="menu-mgmt-page">
            <header className="menu-mgmt-header">
                <div>
                    <h1>🍽️ Digital Wedding Menu</h1>
                    <p>Create categories, add dishes, tag allergens and schedule by day</p>
                </div>
                <button className="menu-add-btn" onClick={() => { setEditing({ ...emptyCategory }); setModalOpen(true); }}>
                    + Add Category
                </button>
            </header>

            {/* Category cards */}
            <div className="menu-cat-grid">
                {categories.length === 0 && (
                    <div className="menu-empty">
                        <span>📋</span>
                        <h3>No menu yet</h3>
                        <p>Tap "Add Category" to start building your wedding menu.</p>
                    </div>
                )}
                {categories.map(cat => (
                    <div key={cat._id} className={`menu-cat-card ${!cat.isActive ? 'inactive' : ''} ${cat.isBeverage ? 'beverage' : ''}`}>
                        <div className="mcc-top">
                            <span className="mcc-icon">{cat.icon}</span>
                            <div className="mcc-info">
                                <h3>{cat.name}</h3>
                                {cat.dayLabel && <span className="mcc-day">{cat.dayLabel}</span>}
                            </div>
                            <div className="mcc-badges">
                                {cat.isBeverage && <span className="mcc-badge bev">🍹 Beverage</span>}
                                <span className={`mcc-badge ${cat.isActive ? 'active' : 'hidden'}`}>
                                    {cat.isActive ? '✅ Active' : '🔒 Hidden'}
                                </span>
                            </div>
                        </div>

                        <div className="mcc-items">
                            {cat.items.length === 0 ? (
                                <p className="mcc-no-items">No items added yet</p>
                            ) : (
                                cat.items.map((item, idx) => (
                                    <div key={idx} className={`mcc-item ${!item.isAvailable ? 'unavailable' : ''}`}>
                                        <div className="mcc-item-left">
                                            <span className={`veg-icon ${item.isVeg ? 'veg' : 'nonveg'}`}></span>
                                            <div>
                                                <span className="mcc-item-name">{item.name}</span>
                                                {item.description && <span className="mcc-item-desc">{item.description}</span>}
                                                {item.tags.length > 0 && (
                                                    <div className="mcc-item-tags">
                                                        {item.tags.map(t => {
                                                            const tagDef = ALLERGEN_TAGS.find(at => at.label === t);
                                                            return <span key={t} className="mcc-tag" style={{ color: tagDef?.color }}>{tagDef?.icon} {t}</span>;
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            className={`avail-toggle ${item.isAvailable ? 'on' : 'off'}`}
                                            onClick={() => handleToggleItem(cat._id!, idx)}
                                            title={item.isAvailable ? 'Mark unavailable' : 'Mark available'}
                                        >
                                            {item.isAvailable ? '✅' : '❌'}
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="mcc-actions">
                            <button onClick={() => { setEditing(cat); setModalOpen(true); }}>✏️ Edit</button>
                            <button className="del" onClick={() => handleDelete(cat._id!)}>🗑️ Delete</button>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Modal ─────────────────────────────── */}
            {modalOpen && (
                <div className="menu-modal-overlay" onClick={() => !saving && setModalOpen(false)}>
                    <div className="menu-modal" onClick={e => e.stopPropagation()}>
                        <button className="menu-modal-close" onClick={() => setModalOpen(false)}>&times;</button>
                        <h2>{editing._id ? 'Edit Category' : 'New Category'}</h2>

                        <form onSubmit={handleSave}>
                            {/* Name + Icon */}
                            <div className="mm-row">
                                <div className="mm-field" style={{ flex: 3 }}>
                                    <label>Category Name *</label>
                                    <input
                                        value={editing.name} required
                                        onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                                        placeholder="e.g. Appetizers, Main Course, Bar"
                                    />
                                </div>
                                <div className="mm-field" style={{ flex: 1 }}>
                                    <label>Icon</label>
                                    <div className="icon-picker">
                                        {CATEGORY_ICONS.map(ic => (
                                            <button key={ic} type="button" className={`icon-opt ${editing.icon === ic ? 'sel' : ''}`}
                                                onClick={() => setEditing(p => ({ ...p, icon: ic }))}>{ic}</button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Day / Scheduling */}
                            <div className="mm-row">
                                <div className="mm-field">
                                    <label>Day Label (multi-day wedding)</label>
                                    <input
                                        value={editing.dayLabel}
                                        onChange={e => setEditing(p => ({ ...p, dayLabel: e.target.value }))}
                                        placeholder="e.g. Day 1 - Mehendi, Day 2 - Sangeet"
                                    />
                                </div>
                                <div className="mm-field">
                                    <label>Scheduled Date</label>
                                    <input
                                        type="date"
                                        min={new Date().toISOString().split('T')[0]}
                                        value={editing.scheduledDate ? editing.scheduledDate.substring(0, 10) : ''}
                                        onChange={e => setEditing(p => ({ ...p, scheduledDate: e.target.value }))}
                                    />
                                </div>
                            </div>

                            {/* Toggles */}
                            <div className="mm-toggles">
                                <label className="mm-toggle-label">
                                    <input type="checkbox" checked={editing.isBeverage} onChange={e => setEditing(p => ({ ...p, isBeverage: e.target.checked }))} />
                                    <span className="mm-chk"></span>
                                    🍹 This is a Beverage / Bar section
                                </label>
                                <label className="mm-toggle-label">
                                    <input type="checkbox" checked={editing.isActive} onChange={e => setEditing(p => ({ ...p, isActive: e.target.checked }))} />
                                    <span className="mm-chk"></span>
                                    Category is Visible to Guests
                                </label>
                            </div>

                            {/* Items */}
                            <div className="mm-items-section">
                                <div className="mm-items-header">
                                    <label>Menu Items ({editing.items.length})</label>
                                    <button type="button" className="mm-add-item" onClick={addItem}>+ Add Dish</button>
                                </div>

                                <div className="mm-items-list">
                                    {editing.items.map((item, idx) => (
                                        <div key={idx} className="mm-item-card">
                                            <div className="mm-item-top">
                                                <div className="mm-item-inputs">
                                                    <input
                                                        value={item.name} required
                                                        onChange={e => updateItem(idx, 'name', e.target.value)}
                                                        placeholder="Dish name (e.g. Paneer Tikka)"
                                                    />
                                                    <textarea
                                                        value={item.description}
                                                        onChange={e => updateItem(idx, 'description', e.target.value)}
                                                        placeholder="A mouth-watering description..."
                                                        rows={2}
                                                    />
                                                </div>
                                                <button type="button" className="mm-remove" onClick={() => removeItem(idx)}>✕</button>
                                            </div>

                                            <div className="mm-item-meta">
                                                <label className="mm-veg-toggle">
                                                    <span className={`veg-dot ${item.isVeg ? 'veg' : 'nonveg'}`}></span>
                                                    <button type="button" onClick={() => updateItem(idx, 'isVeg', !item.isVeg)}>
                                                        {item.isVeg ? 'Veg' : 'Non-Veg'}
                                                    </button>
                                                </label>

                                                <div className="mm-tags-row">
                                                    {ALLERGEN_TAGS.map(tag => (
                                                        <button
                                                            key={tag.label}
                                                            type="button"
                                                            className={`mm-tag-btn ${item.tags.includes(tag.label) ? 'active' : ''}`}
                                                            style={{ '--tag-color': tag.color } as React.CSSProperties}
                                                            onClick={() => toggleTag(idx, tag.label)}
                                                        >
                                                            {tag.icon} {tag.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    {editing.items.length === 0 && <p className="mm-no-items">Click "Add Dish" to start adding menu items</p>}
                                </div>
                            </div>

                            <button type="submit" className="mm-save-btn" disabled={saving}>
                                {saving ? 'Saving...' : '💾 Save Category'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MenuManagement;
